// src/components/common/AnnouncementBanner.tsx

import React, { useState } from "react";
import { FaTimes, FaBullhorn } from "react-icons/fa";
import { useSiteSettings } from "../../context/SiteSettingsContext";
import { useAuth } from "../../context/AuthContext";
import "./AnnouncementBanner.css";

// localStorage key holding the text of the announcement the user dismissed.
// Keyed by TEXT (not a boolean) so that publishing a NEW announcement makes
// the banner reappear, while the same announcement stays hidden after reload.
const DISMISSED_STORAGE_KEY = "dismissedAnnouncement";

/**
 * Site-wide announcement banner.
 * Reads from SiteSettingsContext — shown only to LOGGED-IN users
 * when the admin has enabled the announcement and set a non-empty text.
 * Dismissal is persisted in localStorage per announcement text: once closed,
 * the same announcement never reappears, but a new/edited one does.
 */
export const AnnouncementBanner: React.FC = () => {
    const { announcementEnabled, announcementText } = useSiteSettings();
    const { isAuthenticated } = useAuth();
    // Lazy init — read the persisted dismissed text once on mount
    const [dismissedText, setDismissedText] = useState<string | null>(() => {
        try {
            return localStorage.getItem(DISMISSED_STORAGE_KEY);
        } catch {
            return null;
        }
    });

    const handleDismiss = () => {
        setDismissedText(announcementText);
        try {
            localStorage.setItem(DISMISSED_STORAGE_KEY, announcementText);
        } catch {
            // Storage unavailable (private mode etc.) — session-only dismissal
        }
    };

    // Do not render for guests, if disabled, text is empty, or this exact announcement was dismissed
    if (!isAuthenticated || !announcementEnabled || !announcementText.trim() || dismissedText === announcementText) {
        return null;
    }

    return (
        <div className="announcement-banner">
            <FaBullhorn className="announcement-icon" />
            <span className="announcement-text">{announcementText}</span>
            <button
                className="announcement-close"
                onClick={handleDismiss}
                aria-label="Kapat"
            >
                <FaTimes />
            </button>
        </div>
    );
};